if (!customElements.get('training-reserve')) {
  customElements.define(
    'training-reserve',
    class TrainingReserve extends HTMLElement {
      connectedCallback() {
        this.gallery = this.querySelector('[data-reserve-gallery]');
        this.stage = this.querySelector('[data-reserve-stage]');
        this.stageContainer = this.querySelector('[data-reserve-gallery-stage]');
        this.allThumbs = Array.from(this.querySelectorAll('[data-reserve-thumb]'));
        this.thumbs = this.allThumbs.filter((thumb) => !thumb.hidden);
        this.thumbRail = this.querySelector('[data-reserve-thumb-rail]');
        this.galleryCurrent = this.querySelector('[data-reserve-gallery-current]');
        this.galleryTotal = this.querySelector('[data-reserve-gallery-total]');
        this.galleryCounter = this.galleryCurrent?.closest('.tr__gallery-counter') || null;
        this.galleryStatus = this.querySelector('[data-reserve-gallery-status]');
        this.galleryCaption = this.querySelector('[data-reserve-gallery-caption]');
        this.previousButton = this.querySelector('[data-reserve-prev]');
        this.nextButton = this.querySelector('[data-reserve-next]');
        this.variantSelect = this.querySelector('[data-reserve-variant]');
        this.variantInputs = Array.from(this.querySelectorAll('[data-reserve-tier-input]'));
        this.comparisonButtons = Array.from(this.querySelectorAll('[data-reserve-comparison-select]'));
        this.tierShortcutButtons = Array.from(this.querySelectorAll('[data-reserve-tier-shortcut]'));
        this.primarySubmitButton = this.querySelector('.tr__atc');
        this.inventoryUrgency = this.querySelector('[data-reserve-inventory-urgency]');
        this.inventoryUrgencyCopy = this.querySelector('[data-reserve-inventory-copy]');
        this.stickyBar = this.querySelector('[data-reserve-sticky]');
        this.stickySubmitButton = this.querySelector('[data-reserve-sticky-submit]');
        this.finalSubmitButton = this.querySelector('[data-reserve-final-submit]');
        this.galleryRequestId = 0;
        this.pendingImageIndex = null;

        this.allThumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            const visibleIndex = this.thumbs.indexOf(thumb);
            if (visibleIndex >= 0) this.selectImage(visibleIndex);
          });
        });

        this.previousButton?.addEventListener('click', () => this.stepImage(-1));
        this.nextButton?.addEventListener('click', () => this.stepImage(1));
        this.setupGalleryNavigation();
        this.variantSelect?.addEventListener('change', () => this.updateVariant());
        this.variantInputs.forEach((input) => {
          input.addEventListener('change', () => this.updateVariant(input));
        });
        this.comparisonButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const matchingInput = this.variantInputs.find((input) => input.dataset.sku === button.dataset.sku);
            if (!matchingInput || matchingInput.disabled) return;
            this.selectSource = button.closest('.tr__comparison-mobile') ? 'comparison_table' : 'comparison_grid';
            matchingInput.checked = true;
            matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        this.tierShortcutButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const matchingInput = this.variantInputs.find((input) => input.dataset.sku === button.dataset.sku);
            if (!matchingInput || matchingInput.disabled) return;
            this.selectSource = 'section_shortcut';
            matchingInput.checked = true;
            matchingInput.dispatchEvent(new Event('change', { bubbles: true }));

            const buyArea = this.querySelector('[id^="TrainingReserveBuy-"]');
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            buyArea?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
          });
        });

        this.setupStickyAddToCart();
        this.setupFinalAddToCart();

        if (this.variantInputs.length || this.variantSelect) this.updateVariant();
        this.ready = true;
        this.setupSectionTracking();
      }

      // Piggybacks the store's existing GA4 (gtag), so it inherits whatever
      // consent gate that already honours. Silent when gtag is absent.
      track(name, params) {
        if (typeof window.gtag !== 'function') return;
        window.gtag('event', name, params);
      }

      setupSectionTracking() {
        const named = [
          ['.tr__culture', 'can_collection'],
          ['.tr__comparison', 'comparison'],
          ['.tr__purpose', 'calibers'],
          ['.tr__targets', 'targets'],
          ['.tr__specs', 'specs'],
          ['.tr__details', 'faq'],
          ['.tr__final', 'final_cta'],
        ];
        const targets = named
          .map(([sel, label]) => [this.querySelector(sel), label])
          .filter(([el]) => el);
        if (!targets.length || typeof IntersectionObserver === 'undefined') return;

        const labels = new Map(targets);
        this.sectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const label = labels.get(entry.target);
              this.track('section_view', { section_name: label, page_offer: '556-9mm-range-pile' });
              this.sectionObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.4 }
        );
        targets.forEach(([el]) => this.sectionObserver.observe(el));
      }

      setupStickyAddToCart() {
        if (!this.stickyBar || !this.stickySubmitButton || !this.primarySubmitButton) return;

        this.stickySubmitButton.addEventListener('click', () => {
          if (this.primarySubmitButton.disabled) return;
          this.primarySubmitButton.click();
        });

        const mobileViewport = window.matchMedia('(max-width: 749px)');
        const eagerSticky = this.stickyBar.dataset.eager === 'true';
        const setStickyVisibility = (visible) => {
          const shouldShow = mobileViewport.matches && visible;
          this.stickyBar.classList.toggle('is-visible', shouldShow);
          this.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
          this.stickySubmitButton.tabIndex = shouldShow ? 0 : -1;
        };

        this.stickyObserver = new IntersectionObserver(([entry]) => {
          setStickyVisibility(!entry.isIntersecting && (eagerSticky || entry.boundingClientRect.top < 0));
        });
        this.stickyObserver.observe(this.primarySubmitButton);

        mobileViewport.addEventListener?.('change', () => {
          if (!mobileViewport.matches) setStickyVisibility(false);
        });
      }

      setupFinalAddToCart() {
        if (!this.finalSubmitButton || !this.primarySubmitButton) return;

        this.finalSubmitButton.addEventListener('click', () => {
          if (this.primarySubmitButton.disabled) return;
          this.primarySubmitButton.click();
        });
      }

      updateGalleryVisibility(selectedSku) {
        if (!this.allThumbs.length) return;

        this.allThumbs.forEach((thumb) => {
          const visibleSkus = (thumb.dataset.visibleSkus || '')
            .split(/[|,]/)
            .map((sku) => sku.trim())
            .filter(Boolean);
          const isVisible = !visibleSkus.length || visibleSkus.includes(selectedSku);
          thumb.hidden = !isVisible;
          thumb.setAttribute('aria-hidden', String(!isVisible));
          if (!isVisible) {
            thumb.classList.remove('is-active');
            thumb.setAttribute('aria-pressed', 'false');
          }
        });

        this.thumbs = this.allThumbs.filter((thumb) => !thumb.hidden);
        const imageCount = this.thumbs.length;
        if (this.galleryCurrent) this.galleryCurrent.textContent = imageCount ? '1' : '0';
        if (this.galleryTotal) this.galleryTotal.textContent = String(imageCount);
        if (this.galleryCounter) this.galleryCounter.hidden = imageCount < 2;
        if (this.previousButton) this.previousButton.hidden = imageCount < 2;
        if (this.nextButton) this.nextButton.hidden = imageCount < 2;
        if (this.thumbRail) this.thumbRail.hidden = imageCount < 2;

        this.thumbs.forEach((thumb, index) => {
          thumb.removeAttribute('aria-hidden');
          thumb.setAttribute('aria-label', `View product image ${index + 1} of ${imageCount}`);
        });
      }

      get activeImageIndex() {
        const index = this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
        return index < 0 ? 0 : index;
      }

      setupGalleryNavigation() {
        this.updateGalleryStatus(this.activeImageIndex);
        if (!this.stageContainer || this.thumbs.length < 2) return;

        this.stageContainer.addEventListener('keydown', (event) => {
          if (event.target !== this.stageContainer) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.stepImage(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepImage(1);
          }
        });

        let pointerStart = null;
        this.stageContainer.addEventListener('pointerdown', (event) => {
          if (event.target.closest('button')) return;
          pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
          this.stageContainer.setPointerCapture?.(event.pointerId);
        });
        this.stageContainer.addEventListener('pointerup', (event) => {
          if (!pointerStart || pointerStart.id !== event.pointerId) return;
          const deltaX = event.clientX - pointerStart.x;
          const deltaY = event.clientY - pointerStart.y;
          pointerStart = null;
          if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
          this.stepImage(deltaX < 0 ? 1 : -1);
        });
        this.stageContainer.addEventListener('pointercancel', () => {
          pointerStart = null;
        });
      }

      updateGalleryStatus(index) {
        const selectedThumb = this.thumbs[index];
        if (!selectedThumb) return;
        if (this.galleryCurrent) this.galleryCurrent.textContent = String(index + 1);
        if (this.galleryStatus) {
          const imageLabel = selectedThumb.dataset.alt || 'Product image';
          this.galleryStatus.textContent = `Image ${index + 1} of ${this.thumbs.length}: ${imageLabel}`;
        }
      }

      preloadGalleryImage(source, sourceSet) {
        return new Promise((resolve, reject) => {
          const preloader = new Image();
          let settled = false;
          let timeoutId = null;
          const finish = (callback) => {
            if (settled) return;
            settled = true;
            if (timeoutId) window.clearTimeout(timeoutId);
            preloader.onload = null;
            preloader.onerror = null;
            callback();
          };

          preloader.onload = async () => {
            if (typeof preloader.decode === 'function') {
              try {
                await preloader.decode();
              } catch (error) {
                if (!preloader.naturalWidth) {
                  finish(() => reject(error));
                  return;
                }
              }
            }
            finish(resolve);
          };
          preloader.onerror = () => finish(() => reject(new Error('Gallery image failed to load')));
          preloader.decoding = 'async';
          preloader.sizes = this.stage?.getAttribute('sizes') || '100vw';
          timeoutId = window.setTimeout(
            () => finish(() => reject(new Error('Gallery image load timed out'))),
            15000
          );
          if (sourceSet) preloader.srcset = sourceSet;
          preloader.src = source;
        });
      }

      commitGallerySelection(index) {
        const selectedThumb = this.thumbs[index];
        if (!selectedThumb) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduceMotion && typeof this.stage.animate === 'function') {
          this.stage.animate(
            [
              { opacity: 0.55, transform: 'scale(0.992)' },
              { opacity: 1, transform: 'scale(1)' },
            ],
            { duration: 180, easing: 'ease-out' }
          );
        }

        this.thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === index;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-pressed', String(isActive));
        });
        this.updateGalleryStatus(index);

        if (this.thumbRail && this.thumbRail.scrollWidth > this.thumbRail.clientWidth) {
          selectedThumb.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }

      async selectImage(index) {
        if (!this.stage || !this.thumbs.length) return;

        const normalizedIndex = (index + this.thumbs.length) % this.thumbs.length;
        const selectedThumb = this.thumbs[normalizedIndex];
        if (!selectedThumb) return;

        const source = selectedThumb.dataset.src || '';
        const sourceSet = selectedThumb.dataset.srcset || '';
        if (!source) return;

        const requestId = ++this.galleryRequestId;
        this.pendingImageIndex = normalizedIndex;
        this.gallery?.setAttribute('aria-busy', 'true');

        const mediaIsAlreadyLoaded =
          selectedThumb.dataset.mediaId &&
          selectedThumb.dataset.mediaId === this.stage.dataset.mediaId &&
          this.stage.complete &&
          this.stage.naturalWidth > 0;

        if (!mediaIsAlreadyLoaded) {
          try {
            await this.preloadGalleryImage(source, sourceSet);
          } catch (error) {
            if (requestId !== this.galleryRequestId) return;
            this.pendingImageIndex = null;
            this.gallery?.removeAttribute('aria-busy');
            if (this.galleryStatus) {
              this.galleryStatus.textContent = 'That product image could not be loaded. Showing the current image.';
            }
            return;
          }
        }

        if (requestId !== this.galleryRequestId) return;

        this.stage.srcset = sourceSet;
        this.stage.src = source;
        this.stage.alt = selectedThumb.dataset.alt || '';
        this.stage.dataset.mediaId = selectedThumb.dataset.mediaId || '';
        if (this.galleryCaption) {
          this.galleryCaption.textContent = selectedThumb.dataset.caption || '';
          this.galleryCaption.hidden = !selectedThumb.dataset.caption;
        }
        this.commitGallerySelection(normalizedIndex);
        this.pendingImageIndex = null;
        this.gallery?.removeAttribute('aria-busy');
      }

      stepImage(direction) {
        const currentIndex = Number.isInteger(this.pendingImageIndex) ? this.pendingImageIndex : this.activeImageIndex;
        this.selectImage(currentIndex + direction);
      }

      getInventoryUrgencyDisplayQuantity(inventoryQuantity) {
        if (this.inventoryUrgency?.dataset.stagedCurve !== 'true') return inventoryQuantity;

        if (inventoryQuantity === 16) return 8;
        if (inventoryQuantity === 15) return 7;
        if (inventoryQuantity === 14 || inventoryQuantity === 13) return 4;
        if (inventoryQuantity === 12 || inventoryQuantity === 11) return 3;
        if (inventoryQuantity >= 8 && inventoryQuantity <= 10) return 1;
        if (inventoryQuantity === 7 || inventoryQuantity === 6) return 4;
        if (inventoryQuantity === 5 || inventoryQuantity === 4) return 3;
        if (inventoryQuantity >= 1 && inventoryQuantity <= 3) return 1;

        return inventoryQuantity;
      }

      updateVariant(selectedControl = null) {
        const option =
          selectedControl ||
          this.variantInputs.find((input) => input.checked) ||
          this.variantSelect?.selectedOptions?.[0];
        if (!option) return;

        this.updateGalleryVisibility(option.dataset.sku || '');

        const price = option.dataset.price || '';
        const available = option.dataset.available === 'true';
        const priceTargets = this.querySelectorAll(
          '[data-reserve-price], [data-reserve-button-price], [data-reserve-sticky-price], [data-reserve-final-price], [data-reserve-contents-price]'
        );
        const button = this.querySelector('.tr__atc');
        const buttonLabel = this.querySelector('[data-reserve-button-label]');
        const stickyTitle = this.querySelector('[data-reserve-sticky-title]');
        const stickyShipping = this.querySelector('[data-reserve-sticky-shipping]');
        const selectedButtonLabel = option.dataset.buttonLabel || button?.dataset.buttonLabel || 'Add to cart';

        priceTargets.forEach((target) => {
          target.textContent = available ? price : '';
        });

        // Anchor pricing: compare-at and savings are empty strings until the
        // compare-at values are imported, so hide rather than render a blank slot.
        const comparePrice = available ? option.dataset.comparePrice || '' : '';
        const savingsLabel = available ? option.dataset.savingsLabel || '' : '';
        this.querySelectorAll('[data-reserve-compare-price]').forEach((target) => {
          target.textContent = comparePrice;
          target.hidden = !comparePrice;
        });
        this.querySelectorAll('[data-reserve-savings]').forEach((target) => {
          target.textContent = savingsLabel;
          target.hidden = !savingsLabel;
        });

        if (button) button.disabled = !available;
        if (this.stickySubmitButton) this.stickySubmitButton.disabled = !available;
        if (this.finalSubmitButton) this.finalSubmitButton.disabled = !available;
        if (buttonLabel) buttonLabel.textContent = available ? selectedButtonLabel : 'Sold out';
        if (this.inventoryUrgency) {
          const inventoryQuantity = Number.parseInt(option.dataset.inventoryQuantity || '', 10);
          const inventoryThreshold = Number.parseInt(this.inventoryUrgency.dataset.threshold || '', 10);
          const inventoryIsTracked = option.dataset.inventoryTracked === 'true';
          const urgencyItemLabel = option.dataset.inventoryUrgencyItemLabel || '';
          const inventoryDisplayQuantity = this.getInventoryUrgencyDisplayQuantity(inventoryQuantity);
          const urgencyDisplayItemLabel =
            inventoryDisplayQuantity === 1
              ? urgencyItemLabel.replace('knives remain', 'knife remains')
              : urgencyItemLabel;
          const showInventoryUrgency =
            available &&
            inventoryIsTracked &&
            Number.isFinite(inventoryQuantity) &&
            Number.isFinite(inventoryThreshold) &&
            inventoryQuantity > 0 &&
            inventoryQuantity <= inventoryThreshold;

          this.inventoryUrgency.hidden = !showInventoryUrgency;
          if (this.inventoryUrgencyCopy) {
            this.inventoryUrgencyCopy.textContent = showInventoryUrgency
              ? `Only ${inventoryDisplayQuantity}${urgencyDisplayItemLabel ? ` ${urgencyDisplayItemLabel}` : ' remaining at this tier'}`
              : '';
          }
        }
        if (stickyTitle && option.dataset.stickyLabel) {
          stickyTitle.textContent = option.dataset.stickyLabel;
        }
        if (option.dataset.stickyLabel) {
          this.querySelectorAll('[data-reserve-contents-title], [data-reserve-value-title]').forEach((target) => {
            target.textContent = option.dataset.stickyLabel;
          });
        }
        if (stickyShipping && 'stickyShipping' in option.dataset) {
          stickyShipping.textContent = option.dataset.stickyShipping;
          stickyShipping.hidden = !option.dataset.stickyShipping;
        }

        this.querySelectorAll('[data-reserve-tier-card]').forEach((card) => {
          const input = card.querySelector('[data-reserve-tier-input]');
          const isSelected = input === option;
          const details = card.querySelector('[data-reserve-tier-details]');
          card.classList.toggle('is-selected', isSelected);
          if (details && this.dataset.expandTierDetails === 'true') details.hidden = !isSelected;
        });

        const dynamicContent = {
          heroHook: '[data-reserve-hero-hook]',
          heroHeadline: '[data-reserve-hero-headline]',
          heroLede: '[data-reserve-hero-lede]',
          totalRounds: '[data-reserve-total-rounds]',
          packCount: '[data-reserve-pack-count]',
          sessionCount: '[data-reserve-session-count]',
          includedHighlight: '[data-reserve-included-highlight]',
          shippingNote: '[data-reserve-shipping-note]',
          shippingProof: '[data-reserve-shipping-proof]',
          separateValue: '[data-reserve-separate-value]',
          valueSavings: '[data-reserve-value-savings]',
          ammoCanMark: '[data-reserve-ammo-can-mark]',
          ammoCanTitle: '[data-reserve-ammo-can-title]',
          ammoCanCopy: '[data-reserve-ammo-can-copy]',
          targetsMark: '[data-reserve-targets-mark]',
          targetsTitle: '[data-reserve-targets-title]',
          targetsCopy: '[data-reserve-targets-copy]',
          nudgeHeading: '[data-reserve-nudge-heading]',
          nudgeCopy: '[data-reserve-nudge-copy]',
        };

        Object.entries(dynamicContent).forEach(([dataKey, selector]) => {
          if (!(dataKey in option.dataset)) return;
          this.querySelectorAll(selector).forEach((target) => {
            target.textContent = option.dataset[dataKey];
            if (
              dataKey === 'shippingNote' ||
              dataKey === 'shippingProof' ||
              dataKey === 'heroHook' ||
              dataKey === 'valueSavings'
            ) {
              target.hidden = !option.dataset[dataKey];
            }
          });
        });

        const separateValueWrap = this.querySelector('[data-reserve-separate-wrap]');
        if (separateValueWrap) separateValueWrap.hidden = !option.dataset.separateValue;

        [1, 2, 3].forEach((index) => {
          const attributeSuffix = index === 1 ? '' : `-${index}`;
          const fields = [
            ['rounds', `[data-reserve-content-rounds="${index}"]`],
            ['product', `[data-reserve-content-product="${index}"]`],
            ['packout', `[data-reserve-content-packout="${index}"]`],
          ];

          fields.forEach(([field, selector]) => {
            const value = option.getAttribute(`data-content-${field}${attributeSuffix}`);
            if (!value) return;
            this.querySelectorAll(selector).forEach((target) => {
              target.textContent = value;
            });
          });
        });

        const shippingIsIncluded = option.dataset.shippingIncluded === 'true';
        this.querySelectorAll('[data-reserve-shipping-property]').forEach((propertyInput) => {
          propertyInput.disabled = !shippingIsIncluded;
        });
        this.querySelectorAll('[data-reserve-shipping-sku]').forEach((propertyInput) => {
          propertyInput.value = option.dataset.sku || '';
        });

        this.querySelectorAll('[data-reserve-comparison-card]').forEach((card) => {
          const isSelected = card.dataset.sku === option.dataset.sku;
          card.classList.toggle('is-selected', isSelected);
          const label = card.querySelector('[data-reserve-comparison-label]');
          if (label) label.textContent = isSelected ? 'Selected' : label.dataset.defaultLabel || 'Choose this stockpile';
        });

        this.querySelectorAll('[data-reserve-comparison-column]').forEach((cell) => {
          const isSelected = cell.dataset.sku === option.dataset.sku;
          cell.classList.toggle('is-selected', isSelected);
          const label = cell.querySelector('[data-reserve-comparison-label]');
          if (label) label.textContent = isSelected ? 'Selected' : label.dataset.defaultLabel || 'Choose';
        });

        const selectionNudge = this.querySelector('[data-reserve-selection-nudge]');
        if (selectionNudge) {
          selectionNudge.hidden = !(option.dataset.nudgeHeading || option.dataset.nudgeCopy);
        }

        const valueProof = this.querySelector('[data-reserve-value-proof]');
        if (valueProof) {
          valueProof.hidden = !(option.dataset.separateValue || option.dataset.valueSavings || option.dataset.componentStack);
        }

        const componentStack = this.querySelector('[data-reserve-component-stack]');
        if (componentStack && 'componentStack' in option.dataset) {
          const rows = (option.dataset.componentStack || '')
            .split('|')
            .map((row) => row.trim())
            .filter(Boolean);
          const fragment = document.createDocumentFragment();

          rows.forEach((row) => {
            const [title, ...detailParts] = row.split('::');
            const detail = detailParts.join('::').trim();
            const item = document.createElement('li');
            const heading = document.createElement('strong');
            heading.textContent = title.trim();
            item.append(heading);
            if (detail) {
              const description = document.createElement('span');
              description.textContent = detail;
              item.append(description);
            }
            fragment.append(item);
          });

          componentStack.replaceChildren(fragment);
        }

        if (option.value) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', url);
        }

        if (this.ready) {
          const source = this.selectSource || 'tier_picker';
          this.selectSource = null;
          this.track('select_item', {
            item_list_name: 'Range Pile tiers',
            select_source: source,
            items: [
              {
                item_id: option.dataset.sku || '',
                item_name: option.dataset.stickyLabel || '',
                price: Number.parseFloat((option.dataset.price || '').replace(/[^0-9.]/g, '')) || undefined,
              },
            ],
          });
        }

        const mediaPosition = Number.parseInt(option.dataset.mediaPosition || '', 10);
        const mediaIndexByPosition =
          Number.isInteger(mediaPosition) && mediaPosition > 0 && mediaPosition <= this.thumbs.length
            ? mediaPosition - 1
            : -1;
        const mediaIndexById = option.dataset.mediaId
          ? this.thumbs.findIndex((thumb) => thumb.dataset.mediaId === option.dataset.mediaId)
          : -1;
        const mediaKey = option.dataset.mediaKey || '';
        const mediaIndexByKey = mediaKey
          ? this.thumbs.findIndex((thumb) => (thumb.dataset.src || '').includes(mediaKey))
          : -1;
        const mediaIndex =
          mediaIndexById >= 0 ? mediaIndexById : mediaIndexByPosition >= 0 ? mediaIndexByPosition : mediaIndexByKey;
        if (mediaIndex >= 0) this.selectImage(mediaIndex);
      }
    }
  );
}
